import { useEffect, useState } from "react";
import { CheckCircle2, Copy, Loader2, MessageSquare, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const copy = (text, label) => {
  navigator.clipboard?.writeText(text);
  toast.success(`${label} copied`);
};

// Self-serve Docstate WhatsApp (Meta Cloud API) connection panel — fully isolated
// from the clinic number. Lets the team see the webhook URL + verify token and
// rotate the token / toggle WhatsApp follow-up nudges.
export const ReWhatsAppCard = () => {
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ phone_number_id: "", access_token: "", public_number: "" });
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [leadForm, setLeadForm] = useState({ meta_leadads_token: "", leadad_form_ids: "" });
  const [savingLead, setSavingLead] = useState(false);

  const load = () => api.get("/admin/realestate/whatsapp").then((r) => { setCfg(r.data); setLeadForm((f) => ({ ...f, leadad_form_ids: r.data.leadad_form_ids || "" })); }).catch(() => setCfg({}));
  useEffect(() => { load(); }, []);

  const webhookUrl = cfg ? `${window.location.origin}${cfg.webhook_path || "/api/webhooks/meta/docstate"}` : "";

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/realestate/whatsapp", patch);
      setCfg(data);
      setEdit(false);
      setForm({ phone_number_id: "", access_token: "", public_number: "" });
      toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSaving(false); }
  };

  const testSend = async () => {
    if (!testPhone.trim()) { toast.error("Enter a recipient number"); return; }
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/admin/realestate/whatsapp/test-send", { phone: testPhone });
      setTestRes(data);
      if (data.ok) toast.success("Test message delivered");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setTesting(false); load(); }
  };

  const saveLead = async () => {
    setSavingLead(true);
    try {
      const patch = { leadad_form_ids: leadForm.leadad_form_ids };
      if (leadForm.meta_leadads_token.trim()) patch.meta_leadads_token = leadForm.meta_leadads_token.trim();
      const { data } = await api.put("/admin/realestate/whatsapp", patch);
      setCfg(data);
      setLeadForm({ meta_leadads_token: "", leadad_form_ids: data.leadad_form_ids || "" });
      toast.success("Lead Ads settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSavingLead(false); }
  };

  if (cfg === null) return <div className="glass flex h-24 items-center justify-center rounded-2xl"><Loader2 className="h-5 w-5 animate-spin text-[#C9A24B]" /></div>;

  const Field = ({ label, value, testid }) => (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-white/70 px-3.5 py-2.5">
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#B38B2D]">{label}</p>
        <p className="truncate font-mono text-xs text-[#1A1615]" data-testid={testid}>{value || "—"}</p>
      </div>
      {value && <button onClick={() => copy(value, label)} aria-label={`Copy ${label}`} className="shrink-0 rounded-lg border border-[#EBE3DA] p-2 text-[#5A504B] transition-colors hover:border-[#C9A24B] hover:text-[#1A1615]"><Copy className="h-3.5 w-3.5" /></button>}
    </div>
  );

  return (
    <div className="glass rounded-2xl p-5" data-testid="re-whatsapp-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <MessageSquare className="h-5 w-5 text-[#25D366]" />
          <div>
            <p className="font-serif text-base font-semibold text-[#1A1615]">WhatsApp — Sara live</p>
            <p className="flex items-center gap-1.5 text-xs">
              {cfg.connected
                ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /><span className="text-emerald-600">Connected · {cfg.display_number}</span></>
                : <><XCircle className="h-3.5 w-3.5 text-red-400" /><span className="text-[#91837C]">Not connected</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-[#5A504B]">
            <input type="checkbox" checked={!!cfg.followup_whatsapp} onChange={(e) => save({ followup_whatsapp: e.target.checked })}
              data-testid="re-wa-followup-toggle" className="h-4 w-4 accent-[#25D366]" />
            WhatsApp nudges
          </label>
          <button onClick={() => setEdit((v) => !v)} data-testid="re-wa-edit" className="rounded-full border border-[#C9A24B]/40 px-3.5 py-1.5 text-xs font-medium text-[#8A6D1F] transition-colors hover:bg-[#C9A24B]/10">
            {edit ? "Close" : "Update token"}
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
        <Field label="Webhook Callback URL" value={webhookUrl} testid="re-wa-webhook-url" />
        <Field label="Verify Token" value={cfg.verify_token} testid="re-wa-verify-token" />
      </div>
      <p className="mt-2.5 text-[11px] leading-relaxed text-[#91837C]">
        In Meta → your WhatsApp app → Configuration, paste the Callback URL and Verify Token above, then subscribe to the <b>messages</b> field. Sara replies to inbound WhatsApp automatically; nudges chase quiet leads per your cadence.
      </p>

      {/* Global defaults every NEW conversation inherits (override per-chat in the inbox) */}
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-[#EBE3DA] bg-white/60 px-3.5 py-2.5" data-testid="re-wa-defaults">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#B38B2D]">New-chat defaults</span>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-[#5A504B]">
          <input type="checkbox" checked={cfg.ai_reply_default !== false} onChange={(e) => save({ ai_reply_default: e.target.checked })}
            data-testid="re-wa-reply-default" className="h-4 w-4 accent-[#25D366]" />
          AI Auto-Reply
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-[#5A504B]">
          <input type="checkbox" checked={cfg.ai_followup_default !== false} onChange={(e) => save({ ai_followup_default: e.target.checked })}
            data-testid="re-wa-followup-default" className="h-4 w-4 accent-[#25D366]" />
          AI Follow-ups
        </label>
      </div>

      {/* Diagnostics — why a reply did / didn't arrive */}
      <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
        <div className="rounded-xl bg-white/70 px-3.5 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#B38B2D]">Last inbound</p>
          <p className="text-xs text-[#1A1615]" data-testid="re-wa-last-inbound">
            {cfg.last_inbound ? `${cfg.last_inbound.from} · ${new Date(cfg.last_inbound.at).toLocaleString()}` : "None yet — if you messaged the number and see nothing here, the Meta webhook isn't delivering (recheck Callback URL + Verify Token + subscribe to messages)."}
          </p>
        </div>
        <div className="rounded-xl bg-white/70 px-3.5 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#B38B2D]">Last reply sent</p>
          <p className={`text-xs ${cfg.last_outbound && !cfg.last_outbound.ok ? "text-red-600" : "text-[#1A1615]"}`} data-testid="re-wa-last-outbound">
            {!cfg.last_outbound ? "None yet" : cfg.last_outbound.ok ? `Delivered · ${new Date(cfg.last_outbound.at).toLocaleString()}` : `Failed: ${cfg.last_outbound.error || "unknown"}`}
          </p>
        </div>
      </div>

      {/* One-tap test send */}
      <div className="mt-3 rounded-xl border border-[#EBE3DA] bg-white/60 p-3.5">
        <p className="text-[11px] font-semibold text-[#5A504B]">Send a test message</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Recipient number with country code" data-testid="re-wa-test-phone"
            className="min-w-[220px] flex-1 rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <button onClick={testSend} disabled={testing} data-testid="re-wa-test-send"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-semibold text-white disabled:opacity-60">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />} Send test
          </button>
        </div>
        {testRes && (
          <p className={`mt-2 text-xs ${testRes.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="re-wa-test-result">
            {testRes.ok ? "✓ Delivered — WhatsApp is working." : `✗ Failed: ${testRes.error || "unknown"}`}
            {testRes.hint && <span className="mt-1 block text-[#91837C]">{testRes.hint}</span>}
          </p>
        )}
      </div>

      {/* Meta Lead Ads (Facebook / Instagram) — leads flow straight into the Kanban */}
      <div className="mt-4 rounded-xl border border-[#EBE3DA] bg-white/60 p-3.5" data-testid="re-leadads-card">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold text-[#5A504B]">Meta Lead Ads (Facebook / Instagram)</p>
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${cfg.has_leadads_token ? "bg-emerald-100 text-emerald-700" : "bg-[#F3E9DE] text-[#B38B2D]"}`} data-testid="re-leadads-status">
            {cfg.has_leadads_token ? "Token set" : "Not configured"}
          </span>
        </div>
        {cfg.has_leadads_token && (
          <button onClick={async () => { const { data } = await api.put("/admin/realestate/whatsapp", { meta_leadads_token: "" }); setCfg(data); toast.success("Lead Ads token removed"); }}
            data-testid="re-leadads-clear" className="mt-2 text-[11px] font-medium text-red-500 hover:underline">Remove token</button>
        )}
        <p className="mt-1.5 text-[11px] leading-relaxed text-[#91837C]">
          In Meta → your App → Webhooks, subscribe the Page to the <b>leadgen</b> field using the Callback URL <span className="font-mono">{window.location.origin}{cfg.leadgen_webhook_path || "/api/webhooks/meta"}</span>. New form leads auto-create a lead in your CRM.
        </p>
        <div className="mt-2.5 space-y-2.5">
          <input value={leadForm.meta_leadads_token} onChange={(e) => setLeadForm({ ...leadForm, meta_leadads_token: e.target.value })}
            placeholder={cfg.has_leadads_token ? "New Page Access Token (leave blank to keep current)" : "Page Access Token"} data-testid="re-leadads-token-input"
            className="w-full rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <input value={leadForm.leadad_form_ids} onChange={(e) => setLeadForm({ ...leadForm, leadad_form_ids: e.target.value })}
            placeholder="Lead Form ID(s) — comma-separated (optional filter)" data-testid="re-leadads-forms-input"
            className="w-full rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <button onClick={saveLead} disabled={savingLead} data-testid="re-leadads-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] px-5 py-2.5 text-xs font-semibold text-white disabled:opacity-60">
            {savingLead ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Lead Ads
          </button>
        </div>
      </div>

      {edit && (
        <div className="mt-4 space-y-2.5 border-t border-[#EBE3DA] pt-4">
          <input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
            placeholder={`Phone Number ID (current: ${cfg.phone_number_id || "none"})`} data-testid="re-wa-pnid-input"
            className="w-full rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <input value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })}
            placeholder={cfg.has_token ? "New access token (leave blank to keep current)" : "Meta access token"} data-testid="re-wa-token-input"
            className="w-full rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <input value={form.public_number} onChange={(e) => setForm({ ...form, public_number: e.target.value })}
            placeholder={`Public WhatsApp number for the website button (current: ${cfg.public_number || "none"})`} data-testid="re-wa-public-input"
            className="w-full rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <button onClick={() => save(form)} disabled={saving} data-testid="re-wa-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#1A1615] px-5 py-2.5 text-xs font-medium text-[#F3E5AB] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </button>
        </div>
      )}
    </div>
  );
};
